import React, { useState } from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import type { StepProps } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { MediaUploadField } from '@/src/modules/components/MediaUploadField';
import { RITUAL_TYPES, TEMPLATE_STYLES, ExperienceDSLV10 } from '@/src/modules/experiences/scene/dsl';
import { buildExperienceDsl } from '@/src/modules/experiences/scene/dsl-builder';
import { todayInputValue } from '@/src/infrastructure/lib/date';

const inputClass =
  'w-full rounded-2xl border border-outline-variant bg-surface px-4 py-3.5 outline-none transition-all placeholder:text-on-surface-variant/30 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:[color-scheme:dark]';
const labelClass = 'ml-1 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant';

/** Occasions whose meta requires a second person, mirroring dsl-builder's REQUIRES_SECONDARY_NAME. */
// save_the_date is couple-shaped too; dsl-builder's toOccasion() doesn't know it, so it builds the wedding-shaped v1.0 DSL.
const COUPLE_SHAPED = new Set(['wedding', 'anniversary', 'engagement', 'save_the_date']);

/** Per-occasion copy so this step doesn't say "wedding" for a birthday. */
const OCCASION_COPY: Record<string, {
  primaryLabel: string;
  secondaryLabel?: string;
  primaryPlaceholder: string;
  secondaryPlaceholder?: string;
  datePlaceholder: string;
  eventNamePlaceholder: string;
  taglinePlaceholder: string;
}> = {
  wedding: { primaryLabel: "Bride's First Name", secondaryLabel: "Groom's First Name", primaryPlaceholder: 'e.g. Priya', secondaryPlaceholder: 'e.g. Rahul', datePlaceholder: 'Wedding Date', eventNamePlaceholder: "e.g. Priya & Rahul's Wedding", taglinePlaceholder: 'e.g. The Beginning of Forever' },
  save_the_date: { primaryLabel: "Bride's First Name", secondaryLabel: "Groom's First Name", primaryPlaceholder: 'e.g. Priya', secondaryPlaceholder: 'e.g. Rahul', datePlaceholder: 'Wedding Date', eventNamePlaceholder: "e.g. Priya & Rahul — Save the Date", taglinePlaceholder: 'e.g. Something Beautiful Is Coming' },
  anniversary: { primaryLabel: 'Partner 1 Name', secondaryLabel: 'Partner 2 Name', primaryPlaceholder: 'e.g. Priya', secondaryPlaceholder: 'e.g. Rahul', datePlaceholder: 'Anniversary Date', eventNamePlaceholder: 'e.g. 25th Wedding Anniversary', taglinePlaceholder: 'e.g. Still Going Strong' },
  engagement: { primaryLabel: 'Partner 1 Name', secondaryLabel: 'Partner 2 Name', primaryPlaceholder: 'e.g. Priya', secondaryPlaceholder: 'e.g. Rahul', datePlaceholder: 'Engagement Date', eventNamePlaceholder: "e.g. Priya & Rahul's Engagement", taglinePlaceholder: 'e.g. The Promise of Forever' },
  birthday: { primaryLabel: "Celebrant's Name", primaryPlaceholder: 'e.g. Mia', datePlaceholder: 'Birthday Date', eventNamePlaceholder: "e.g. Mia's 5th Birthday", taglinePlaceholder: "e.g. Let's Celebrate Mia!" },
  baby_shower: { primaryLabel: "Parent-to-be's Name", primaryPlaceholder: 'e.g. Anjali', datePlaceholder: 'Shower Date', eventNamePlaceholder: "e.g. Anjali's Baby Shower", taglinePlaceholder: 'e.g. A Little Miracle on the Way' },
  send_off: { primaryLabel: "Honoree's Name", primaryPlaceholder: 'e.g. Rahul', datePlaceholder: 'Send-off Date', eventNamePlaceholder: "e.g. Rahul's Farewell", taglinePlaceholder: 'e.g. Onward to New Beginnings' },
  custom: { primaryLabel: "Host's Name", primaryPlaceholder: 'e.g. The Sharma Family', datePlaceholder: 'Event Date', eventNamePlaceholder: 'e.g. The Sharma Family Reunion', taglinePlaceholder: 'e.g. Join Us for a Celebration' },
};

type OccasionCopy = (typeof OCCASION_COPY)[string];

/**
 * Turns buildExperienceDsl's raw Zod issues (e.g. "meta.groomName: Too small: expected
 * string to have >=1 characters") into occasion-aware, human copy — using the same
 * primary/secondary labels the form itself shows, so "Groom's First Name is required"
 * reads like the label the user is looking at, not the DSL's internal field name.
 * When both names are missing, combines them into one line instead of two, since that's
 * the common case (nobody has filled in either name yet).
 */
function friendlyDslErrors(
  errors: { path: (string | number)[]; message: string }[],
  copy: OccasionCopy,
): string[] {
  const NAME_FIELDS = new Set(['brideName', 'groomName', 'primaryName', 'secondaryName']);
  const missingNameFields = new Set(
    errors
      .filter((e) => NAME_FIELDS.has(String(e.path[e.path.length - 1])))
      .map((e) => String(e.path[e.path.length - 1])),
  );
  const primaryMissing = missingNameFields.has('brideName') || missingNameFields.has('primaryName');
  const secondaryMissing = missingNameFields.has('groomName') || missingNameFields.has('secondaryName');

  const friendly: string[] = [];
  // copy.secondaryLabel can be absent (e.g. birthday/custom, which don't collect a second
  // name at all) even though a stale draft's persisted experience blob still requires one —
  // never interpolate it unguarded, or the user sees the literal string "undefined is
  // required". Fall back to the raw path in that shouldn't-happen case instead of guessing.
  if (primaryMissing && secondaryMissing && copy.secondaryLabel) {
    friendly.push(
      `${copy.primaryLabel} and ${copy.secondaryLabel} are both required — please fill in both names to continue.`,
    );
  } else if (primaryMissing && !secondaryMissing) {
    friendly.push(`${copy.primaryLabel} is required — please fill it in to continue.`);
  } else if (secondaryMissing && !primaryMissing && copy.secondaryLabel) {
    friendly.push(`${copy.secondaryLabel} is required — please fill it in to continue.`);
  } else if (primaryMissing || secondaryMissing) {
    // Only reachable when secondaryMissing is true but copy.secondaryLabel is absent —
    // the occasion's own copy doesn't have a name for this field, so don't guess.
    for (const e of errors.filter((e) => NAME_FIELDS.has(String(e.path[e.path.length - 1])))) {
      friendly.push(`${e.path.join('.')}: ${e.message}`);
    }
  }

  // Any other issue (unlikely — the form provides sane defaults for everything else)
  // falls back to the raw path so it's never silently dropped.
  const otherErrors = errors.filter((e) => !NAME_FIELDS.has(String(e.path[e.path.length - 1])));
  for (const e of otherErrors) friendly.push(`${e.path.join('.')}: ${e.message}`);

  return friendly;
}

export function ExperienceDetailsStep({ onNext, onBack, data, updateData, draftId, saveDraftNow }: StepProps) {
  const [errors, setErrors] = useState<string[]>([]);

  const occasion = (data.occasionType || 'wedding').toLowerCase();
  // Save the Date collects the same bride/groom/wedding-date meta as a wedding (see COUPLE_SHAPED note).
  const isWedding = occasion === 'wedding' || occasion === 'save_the_date';
  // Save the Date is a teaser card: couple names, event name, date, venue and one couple photo. It shows no
  // host, tagline, ceremony schedule or per-person photos, so the form must not ask for them (TEMPLATE_PITFALLS §4).
  const isSaveTheDate = occasion === 'save_the_date';
  const needsSecondary = COUPLE_SHAPED.has(occasion);
  const copy = OCCASION_COPY[occasion] || OCCASION_COPY.custom;

  // Wedding keeps its own v1.0 meta shape (brideName/groomName/weddingDate) — untouched,
  // so its behaviour and snapshots stay byte-identical. Every other occasion collects the
  // same information under generic field names; dsl-builder's buildV11Dsl already knows
  // how to turn primaryName/secondaryName/eventDate into the right v1.1 DSL.
  const expData: Partial<ExperienceDSLV10> = (data.customData?.experience as any) || {};
  const [splitPrimary, splitSecondary] = data.hostName
    ? [data.hostName.split(/ (?:&|and) /i)[0] || '', data.hostName.split(/ (?:&|and) /i)[1] || '']
    : ['', ''];

  const meta = (expData.meta || (isWedding ? {
    brideName: splitPrimary,
    groomName: splitSecondary,
    weddingDate: data.eventDate ? new Date(data.eventDate).toISOString().split('T')[0] : todayInputValue(),
    tagline: data.customMessage || '',
    referenceImages: {},
  } : {
    primaryName: needsSecondary ? splitPrimary : (data.eventName || splitPrimary),
    secondaryName: needsSecondary ? splitSecondary : undefined,
    eventDate: data.eventDate ? new Date(data.eventDate).toISOString().split('T')[0] : todayInputValue(),
    tagline: data.customMessage || '',
    referenceImages: {},
  })) as any;

  const primaryValue = isWedding ? meta.brideName : meta.primaryName;
  const secondaryValue = isWedding ? meta.groomName : meta.secondaryName;
  const dateValue = isWedding ? meta.weddingDate : meta.eventDate;
  const primaryKey = isWedding ? 'brideName' : 'primaryName';
  const secondaryKey = isWedding ? 'groomName' : 'secondaryName';
  const dateKey = isWedding ? 'weddingDate' : 'eventDate';

  const template = expData.template || 'royal';

  // Helper to update the experience state
  const setExpState = (newExpState: Partial<ExperienceDSLV10>) => {
    const nextExp = { ...expData, ...newExpState };
    updateData({
      customData: {
        ...data.customData,
        experience: nextExp as any,
      }
    });
  };

  // Photo uploads finish asynchronously. Merge against the latest wizard state (functional update), not the
  // `meta` captured at render, or a slow upload overwrites a photo that finished meanwhile.
  const setRefImage = (key: 'bride' | 'groom' | 'couple', url: string) => {
    updateData((prev) => {
      const exp = ((prev.customData?.experience as any) || {}) as Record<string, any>;
      const m = (exp.meta || meta) as Record<string, any>;
      return {
        customData: {
          ...prev.customData,
          experience: { ...exp, meta: { ...m, referenceImages: { ...(m.referenceImages || {}), [key]: url } } },
        } as any,
      };
    });
  };

  const handleNext = () => {
    // The backend's publish guard requires eventName/eventVenue/hostName on the
    // Invitation row itself (invitation.service.ts), independent of whatever this step
    // writes into customData.experience. This step never collected them, so every
    // AI-native invitation — wedding included — failed at Publish with a 400 "missing
    // required fields" that the wizard never surfaced until that final click.
    const missing: string[] = [];
    if (!data.eventName?.trim()) missing.push('Event Name');
    // Save the Date has no "Hosted By" field; the publish guard still needs hostName, so use the couple's names.
    const coupleHost = [meta.brideName, meta.groomName].filter(Boolean).join(' & ');
    // Always re-derive for Save the Date: the field is hidden, so a stale hostName (from an earlier template or
    // an earlier pass through this step) could never be corrected by the user.
    const effectiveHost = isSaveTheDate ? coupleHost || data.hostName?.trim() || '' : data.hostName?.trim() || '';
    if (!effectiveHost) missing.push(isSaveTheDate ? 'Bride and Groom names' : 'Hosted By');
    if (!data.eventVenue?.trim()) missing.push('Venue');
    if (missing.length > 0) {
      setErrors([`Please fill in: ${missing.join(', ')}`]);
      return;
    }

    if (isSaveTheDate && effectiveHost !== data.hostName) updateData({ hostName: effectiveHost });
    const result = buildExperienceDsl(isSaveTheDate ? { ...data, hostName: effectiveHost } : data);
    if (!result.success && result.errors) {
      setErrors(friendlyDslErrors(result.errors, copy));
      return;
    }

    if (result.data) {
      setExpState(result.data as unknown as ExperienceDSLV10);
    }

    // `dateValue` (meta.eventDate / meta.weddingDate) lives only inside the
    // Experience DSL's customData — it was never propagated to the flat
    // `data.eventDate` / `data._dateInput` fields the rest of the wizard reads
    // (autosave's buildDraftPayload, StepPublish's fallback create call,
    // InvitationPublishPreviewCard, DraftPreviewModal). For every AI-native
    // template, `data.eventDate` stayed permanently undefined, so any path
    // that read it directly (StepPublish.tsx's `data.eventDate!` fallback)
    // sent no eventDate at all and the backend rejected it with "eventDate
    // must be a Date instance" — the exact same DTO validation `StepDetails`
    // avoids by doing this same sync (see its handleSubmit).
    if (dateValue) {
      updateData({
        _dateInput: dateValue,
        eventDate: new Date(`${dateValue}T${data._timeInput || '00:00'}`).toISOString(),
      });
    }

    setErrors([]);
    onNext();
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-12 pb-24">
      <div className="text-center">
        <h2 className="text-4xl font-serif text-brand-deep">Cinematic Details</h2>
        <p className="text-on-surface-variant mt-2 text-lg">Let AI craft your perfect {occasion.replace(/_/g, ' ')} story.</p>
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 text-red-600 p-4 rounded-2xl border border-red-100 space-y-2">
          <p className="font-bold flex items-center gap-2">
            <Icons.Warning size={18} /> Please fix these issues:
          </p>
          <ul className="list-disc pl-5 text-sm">
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-8">
        {/* Feeds the Invitation row directly (invitation.eventName/eventVenue/hostName) —
            the backend's publish guard requires these regardless of what the AI-specific
            fields below write into customData.experience. */}
        <div className="space-y-6 pb-6 border-b border-outline-variant/30">
          <div className="space-y-2">
            <label className={labelClass}>Event Name</label>
            <input
              id="eventName"
              className={inputClass}
              value={data.eventName || ''}
              onChange={(e) => updateData({ eventName: e.target.value })}
              placeholder={copy.eventNamePlaceholder}
            />
          </div>
          {!isSaveTheDate && (
          <div className="space-y-2">
            <label className={labelClass}>Hosted By</label>
            <input
              id="hostName"
              className={inputClass}
              value={data.hostName || ''}
              onChange={(e) => updateData({ hostName: e.target.value })}
              placeholder="e.g. The Sharma Family"
            />
          </div>
          )}
          <div className="space-y-2">
            <label className={labelClass}>Venue Name & Address</label>
            <input
              id="eventVenue"
              className={inputClass}
              value={data.eventVenue || ''}
              onChange={(e) => updateData({ eventVenue: e.target.value })}
              placeholder="e.g. The Grand Venue, Mumbai"
            />
          </div>
        </div>

        <div className={cn('grid gap-6', needsSecondary ? 'grid-cols-2' : 'grid-cols-1')}>
          <div className="space-y-2">
            <label className={labelClass}>{copy.primaryLabel}</label>
            <input
              className={inputClass}
              value={primaryValue || ''}
              onChange={(e) => setExpState({ meta: { ...meta, [primaryKey]: e.target.value } })}
              placeholder={copy.primaryPlaceholder}
            />
          </div>
          {needsSecondary && (
            <div className="space-y-2">
              <label className={labelClass}>{copy.secondaryLabel}</label>
              <input
                className={inputClass}
                value={secondaryValue || ''}
                onChange={(e) => setExpState({ meta: { ...meta, [secondaryKey]: e.target.value } })}
                placeholder={copy.secondaryPlaceholder}
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className={labelClass}>{copy.datePlaceholder}</label>
          <input
            type="date"
            className={inputClass}
            value={dateValue || ''}
            onChange={(e) => setExpState({ meta: { ...meta, [dateKey]: e.target.value } })}
          />
        </div>

        {!isSaveTheDate && (
        <div className="space-y-2">
          <label className={labelClass}>Tagline (Optional)</label>
          <input
            className={inputClass}
            value={meta.tagline || ''}
            onChange={(e) => setExpState({ meta: { ...meta, tagline: e.target.value } })}
            placeholder={copy.taglinePlaceholder}
          />
        </div>
        )}

        {/* Ceremonies & Event Days Schedule for Wedding / Multi-event templates */}
        {needsSecondary && !isSaveTheDate && (
          <div className="space-y-4 pt-6 border-t border-outline-variant/30">
            <div className="flex items-center justify-between">
              <div>
                <label className={labelClass}>Ceremonies & Event Days Schedule</label>
                <p className="text-xs text-on-surface-variant/80 mt-0.5">
                  Customize the timing, date, and venue for each ceremony (Kirtan, Haldi, Sangeet, Vivah, Reception).
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {((data.customData?.wedding as any)?.weddingEvents || (data.customData as any)?.events || [
                { name: 'Ganesh Poojan & Kirtan', date: dateValue || '2026-11-11', time: '08:00 AM Onwards', venue: data.eventVenue || '' },
                { name: 'Haldi Ceremony', date: dateValue || '2026-11-12', time: '09:00 AM Onwards', venue: data.eventVenue || '' },
                { name: 'Mehendi Ceremony', date: dateValue || '2026-11-12', time: '04:00 PM Onwards', venue: data.eventVenue || '' },
                { name: 'Sangeet Night', date: dateValue || '2026-11-13', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                { name: 'Shubh Vivah (Wedding)', date: dateValue || '2026-11-14', time: '12:35 PM (Shubh Muhurat)', venue: data.eventVenue || '' },
                { name: 'Grand Reception', date: dateValue || '2026-11-14', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
              ]).map((eventItem: any, idx: number) => (
                <div key={idx} className="p-4 rounded-2xl border border-outline-variant/30 bg-surface-low space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase text-on-surface-variant">Ceremony Name</label>
                      <input
                        className={inputClass}
                        value={eventItem.name || ''}
                        onChange={(e) => {
                          const currentEvents = [...((data.customData?.wedding as any)?.weddingEvents || (data.customData as any)?.events || [
                            { name: 'Ganesh Poojan & Kirtan', date: dateValue || '2026-11-11', time: '08:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Haldi Ceremony', date: dateValue || '2026-11-12', time: '09:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Mehendi Ceremony', date: dateValue || '2026-11-12', time: '04:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Sangeet Night', date: dateValue || '2026-11-13', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Shubh Vivah (Wedding)', date: dateValue || '2026-11-14', time: '12:35 PM (Shubh Muhurat)', venue: data.eventVenue || '' },
                            { name: 'Grand Reception', date: dateValue || '2026-11-14', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                          ])];
                          currentEvents[idx] = { ...currentEvents[idx], name: e.target.value };
                          updateData({
                            customData: {
                              ...(data.customData || {}),
                              events: currentEvents,
                              wedding: {
                                ...(data.customData?.wedding || {}),
                                weddingEvents: currentEvents,
                              },
                            },
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-on-surface-variant">Time / Muhurat</label>
                      <input
                        className={inputClass}
                        value={eventItem.time || ''}
                        onChange={(e) => {
                          const currentEvents = [...((data.customData?.wedding as any)?.weddingEvents || (data.customData as any)?.events || [
                            { name: 'Ganesh Poojan & Kirtan', date: dateValue || '2026-11-11', time: '08:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Haldi Ceremony', date: dateValue || '2026-11-12', time: '09:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Mehendi Ceremony', date: dateValue || '2026-11-12', time: '04:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Sangeet Night', date: dateValue || '2026-11-13', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Shubh Vivah (Wedding)', date: dateValue || '2026-11-14', time: '12:35 PM (Shubh Muhurat)', venue: data.eventVenue || '' },
                            { name: 'Grand Reception', date: dateValue || '2026-11-14', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                          ])];
                          currentEvents[idx] = { ...currentEvents[idx], time: e.target.value };
                          updateData({
                            customData: {
                              ...(data.customData || {}),
                              events: currentEvents,
                              wedding: {
                                ...(data.customData?.wedding || {}),
                                weddingEvents: currentEvents,
                              },
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase text-on-surface-variant">Date</label>
                      <input
                        type="text"
                        placeholder="e.g. 12th November 2026"
                        className={inputClass}
                        value={eventItem.date || ''}
                        onChange={(e) => {
                          const currentEvents = [...((data.customData?.wedding as any)?.weddingEvents || (data.customData as any)?.events || [
                            { name: 'Ganesh Poojan & Kirtan', date: dateValue || '2026-11-11', time: '08:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Haldi Ceremony', date: dateValue || '2026-11-12', time: '09:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Mehendi Ceremony', date: dateValue || '2026-11-12', time: '04:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Sangeet Night', date: dateValue || '2026-11-13', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Shubh Vivah (Wedding)', date: dateValue || '2026-11-14', time: '12:35 PM (Shubh Muhurat)', venue: data.eventVenue || '' },
                            { name: 'Grand Reception', date: dateValue || '2026-11-14', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                          ])];
                          currentEvents[idx] = { ...currentEvents[idx], date: e.target.value };
                          updateData({
                            customData: {
                              ...(data.customData || {}),
                              events: currentEvents,
                              wedding: {
                                ...(data.customData?.wedding || {}),
                                weddingEvents: currentEvents,
                              },
                            },
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-on-surface-variant">Venue Address (Optional)</label>
                      <input
                        placeholder="Leave blank to use primary venue"
                        className={inputClass}
                        value={eventItem.venue || ''}
                        onChange={(e) => {
                          const currentEvents = [...((data.customData?.wedding as any)?.weddingEvents || (data.customData as any)?.events || [
                            { name: 'Ganesh Poojan & Kirtan', date: dateValue || '2026-11-11', time: '08:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Haldi Ceremony', date: dateValue || '2026-11-12', time: '09:00 AM Onwards', venue: data.eventVenue || '' },
                            { name: 'Mehendi Ceremony', date: dateValue || '2026-11-12', time: '04:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Sangeet Night', date: dateValue || '2026-11-13', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                            { name: 'Shubh Vivah (Wedding)', date: dateValue || '2026-11-14', time: '12:35 PM (Shubh Muhurat)', venue: data.eventVenue || '' },
                            { name: 'Grand Reception', date: dateValue || '2026-11-14', time: '07:00 PM Onwards', venue: data.eventVenue || '' },
                          ])];
                          currentEvents[idx] = { ...currentEvents[idx], venue: e.target.value };
                          updateData({
                            customData: {
                              ...(data.customData || {}),
                              events: currentEvents,
                              wedding: {
                                ...(data.customData?.wedding || {}),
                                weddingEvents: currentEvents,
                              },
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Only show Visual Style selector if template does NOT have a pre-configured theme */}
        {!data.templateId?.startsWith('exp-') &&
         !data.templateId?.startsWith('video-') &&
         !data.templateId?.includes('experience') && (
          <div className="space-y-4">
            <label className={labelClass}>Visual Style</label>

            <div className="grid grid-cols-3 gap-4">
              {TEMPLATE_STYLES.map(style => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setExpState({ template: style })}
                  className={cn(
                    "p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2",
                    template === style ? "border-primary bg-primary/5 text-primary" : "border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
                  )}
                >
                  <span className="font-bold capitalize">{style}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Photo Upload Section */}
        <div className="space-y-4 pt-6 border-t border-outline-variant/30">
          <label className={labelClass}>
            {data.templateId?.startsWith('video-')
              ? 'Couple & Showcase Photos (Optional)'
              : occasion === 'birthday'
              ? 'Birthday Photo (Optional)'
              : 'Reference Photos (Optional)'}
          </label>
          {data.templateId?.startsWith('video-') && (
            <p className="text-xs text-on-surface-variant/80 -mt-2">
              Uploaded photos will be displayed directly inside your invitation card sections.
            </p>
          )}
          <div className={cn('grid grid-cols-1 gap-6', needsSecondary && !isSaveTheDate ? 'md:grid-cols-3' : 'md:grid-cols-1')}>
            {isWedding ? (
              <>
                {!isSaveTheDate && (<>
                <MediaUploadField
                  id="ref-bride"
                  label="Bride"
                  value={meta.referenceImages?.bride || ''}
                  onChange={(url) => setRefImage('bride', url)}
                  invitationId={draftId}
                  ensureInvitationId={saveDraftNow}
                />
                <MediaUploadField
                  id="ref-groom"
                  label="Groom"
                  value={meta.referenceImages?.groom || ''}
                  onChange={(url) => setRefImage('groom', url)}
                  invitationId={draftId}
                  ensureInvitationId={saveDraftNow}
                />
                </>)}
                <MediaUploadField
                  id="ref-couple"
                  label="Couple"
                  value={meta.referenceImages?.couple || ''}
                  onChange={(url) => setRefImage('couple', url)}
                  invitationId={draftId}
                  ensureInvitationId={saveDraftNow}
                />
              </>
            ) : occasion === 'birthday' ? (
              <MediaUploadField
                id="ref-birthday-photo"
                label="Celebrant / Birthday Person Photo"
                value={meta.personPhotoUrl || meta.referenceImages?.primary || ''}
                onChange={(url) =>
                  setExpState({
                    meta: {
                      ...meta,
                      personPhotoUrl: url,
                      referenceImages: { ...meta.referenceImages, primary: url },
                    },
                  })
                }
                invitationId={draftId}
                ensureInvitationId={saveDraftNow}
              />
            ) : (
              <>
                <MediaUploadField
                  id="ref-primary"
                  label={copy.primaryLabel}
                  value={meta.referenceImages?.primary || ''}
                  onChange={(url) => setExpState({ meta: { ...meta, referenceImages: { ...meta.referenceImages, primary: url } } })}
                  invitationId={draftId}
                  ensureInvitationId={saveDraftNow}
                />
                {needsSecondary && (
                  <>
                    <MediaUploadField
                      id="ref-secondary"
                      label={copy.secondaryLabel}
                      value={meta.referenceImages?.secondary || ''}
                      onChange={(url) => setExpState({ meta: { ...meta, referenceImages: { ...meta.referenceImages, secondary: url } } })}
                      invitationId={draftId}
                      ensureInvitationId={saveDraftNow}
                    />
                    <MediaUploadField
                      id="ref-together"
                      label="Together"
                      value={meta.referenceImages?.together || ''}
                      onChange={(url) => setExpState({ meta: { ...meta, referenceImages: { ...meta.referenceImages, together: url } } })}
                      invitationId={draftId}
                      ensureInvitationId={saveDraftNow}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-8 border-t border-outline-variant/20">
        <Button variant="ghost" onClick={onBack} className="text-on-surface-variant hover:text-on-surface hover:bg-surface-low rounded-2xl h-14 px-8">
          Back
        </Button>
        <Button onClick={handleNext} className="bg-primary hover:bg-primary-dark text-white rounded-2xl h-14 px-10 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all font-bold text-lg flex items-center gap-2 group">
          Review <Icons.ArrowRight className="group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
}
